import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ConfidenceMonitoringService } from 'src/app/services/confidence-monitoring.service';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { BranchService } from '../services/branch.service';

interface Controller {
  _id: string;
  name: string;
  location: {
    x: string;
    y: string;
  };
  zone: string;
  branch: string;
  controlType?: string;
  dataType?: 'switch' | 'analog';
  icon?: string;
  color?: string;
}

interface ZoneControllers {
  name: string;
  controllers: Controller[];
}

interface ZoneControllersMap {
  [zoneId: string]: ZoneControllers;
}

@Component({
  selector: 'app-branch-controls',
  templateUrl: './branch-controls.component.html',
  styleUrls: ['./branch-controls.component.scss']
})

export class BranchControlsComponent implements OnInit {
  displayedColumns = [
    'Sites',
    'Region',
    'Status'
  ];

  controlTypes = ['BACnet', 'HTTP-Request'];

  public getSitesChangedSubscriper: Subscription;
  selectedZone: string | null = null;
  selectedControlType: string | null = null;
  selectedControl: string | null = null;
  availableControls: Controller[] = [];
  zones: { value: string; name: string; }[] = [];
  digitalValue = false;
  analogValue = 0;
  currentBranchId: string = '';
  executingId: string;
  executeMessage: string;
  executeSuccess = false;
  controllerState: { [id: string]: 'success' | 'error' } = {};
  private allControllers: ZoneControllersMap = {};

  constructor(
    public confidenceMonitoringService: ConfidenceMonitoringService,
    public branchService: BranchService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef) { }

  ngOnInit() {
    this.getSitesChangedSubscriper = this.confidenceMonitoringService.getSitesChangedNotification().subscribe(async (branch: any) => {
      this.currentBranchId = branch.id;
      await this.getBranchControllers(branch.id);
    });

    this.availableControls = [];
  }

  async getBranchControllers(branchId: string) {
    try {
      const data = await this.branchService.getBranchControllers(branchId);
      this.allControllers = Array.isArray(data) ? {} : (data || {});
      this.zones = Object.entries(this.allControllers || {}).map(([zoneId, zoneData]) => ({
        value: zoneId,
        name: zoneData.name
      }));

      this.availableControls = [];
      this.selectedZone = null;
      this.selectedControlType = null;
      this.selectedControl = null;
      this.resetExecuteState();
    } catch (error) {
      console.error('Failed to fetch controllers:', error);
      this.zones = [];
      this.availableControls = [];
      this.allControllers = {};
      this.selectedZone = null;
      this.selectedControlType = null;
      this.selectedControl = null;
      this.resetExecuteState();
    }
  }

  ngOnDestroy() {
    this.getSitesChangedSubscriper.unsubscribe();
  }

  onZoneChange(zoneValue: string) {
    this.selectedZone = zoneValue;
    const zoneControllers = this.allControllers?.[zoneValue]?.controllers || [];
    const hasHttpRequest = zoneControllers.some((controller) => controller.controlType === 'HTTP-Request');
    if (hasHttpRequest) {
      this.onControlTypeChange('HTTP-Request');
      return;
    }
    this.selectedControlType = null;
    this.selectedControl = null;
    this.availableControls = [];
    this.resetExecuteState();
  }

  onControlTypeChange(controlType: string) {
    this.selectedControlType = controlType;
    this.selectedControl = null;
    this.resetExecuteState();
    const zoneControllers = this.allControllers?.[this.selectedZone]?.controllers || [];
    this.availableControls = zoneControllers.filter((controller) => controller.controlType === controlType);
  }

  async onControllerClick(controller: Controller) {
    if (!controller) return;
    if (controller.controlType === 'HTTP-Request') {
      await this.executeController(controller);
      return;
    }
    await this.activateBacnet(controller);
  }

  async activateBacnet(controller: Controller) {
    this.selectedControl = controller._id;
    this.executeMessage = '';
    const value = await this.branchService.readControllerValue(controller._id);
    if (value === null) {
      this.digitalValue = false;
      this.analogValue = 0;
      return;
    }

    if (controller.dataType === 'switch') {
      this.digitalValue = value;
    } else if (controller.dataType === 'analog') {
      this.analogValue = parseFloat(value);
    }
  }

  getSelectedControlType(): 'analog' | 'switch' | null {
    if (!this.selectedControl || this.selectedControlType !== 'BACnet') return null;

    const selectedController = this.availableControls.find(
      control => control._id === this.selectedControl
    );

    return selectedController?.dataType || null;
  }

  buttonIcon(controller: Controller): string {
    if (controller.icon) return controller.icon;
    if (controller.dataType === 'switch') return 'fa-toggle-on';
    if (controller.dataType === 'analog') return 'fa-sliders';
    return 'fa-cog';
  }

  buttonColor(controller: Controller): string | null {
    if (controller.color) return controller.color;
    if (controller.controlType === 'BACnet' && controller.dataType !== 'analog') return '#38b3ef';
    return null;
  }

  async onDigitalChange(event: Event) {
    const checkbox = event.target as HTMLInputElement;
    this.digitalValue = checkbox.checked;
    await this.branchService.setControllerValue(this.selectedControl, this.digitalValue.toString());
  }

  async onAnalogChange(event: any) {
    await this.branchService.setControllerValue(this.selectedControl, this.analogValue.toString());
  }

  async executeController(controller: Controller) {
    if (this.executingId || !controller?._id) return;

    this.selectedControl = controller._id;
    this.executingId = controller._id;
    this.executeMessage = '';
    delete this.controllerState[controller._id];
    this.cdr.detectChanges();

    try {
      const response = await this.branchService.executeController(controller._id);
      const result = response?.data;
      const succeeded = response?.success === true && result?.success === true;
      this.applyExecuteResult(controller, succeeded);
    } catch (error) {
      this.applyExecuteResult(controller, false);
    } finally {
      this.executingId = null;
      this.cdr.detectChanges();
    }
  }

  private applyExecuteResult(controller: Controller, succeeded: boolean) {
    this.controllerState[controller._id] = succeeded ? 'success' : 'error';
    this.executeSuccess = succeeded;
    this.executeMessage = this.translate.instant(
      succeeded ? 'CONTROLLERS.EXECUTE_SUCCESS' : 'CONTROLLERS.EXECUTE_FAILED'
    );
  }

  dismissExecuteMessage(): void {
    this.executeMessage = '';
  }

  private resetExecuteState() {
    this.executingId = null;
    this.executeMessage = '';
    this.executeSuccess = false;
    this.controllerState = {};
  }

}
